import React from "react";

import {
  productSliderStyles,
  responsiveCss,
} from "../ProductSlider/ProductSlider.styles";
import {
  ProductSliderBaseProps,
  ProductSliderCard,
} from "../ProductSlider/ProductSlider.shared";
import { applyProductCollectionFilter } from "../ProductSlider/productCollection.shared";
import ProductEmptyState from "../ProductEmptyState/ProductEmptyState";
import {
  getProductSliderScrollBottomCtaButtonStyle,
  getProductSliderScrollBottomCtaWrapStyle,
  productSliderScrollStyles,
} from "./ProductSliderScroll.styles";
import type {
  ProductSliderScrollBottomCtaAlign,
  ProductSliderScrollBottomCtaVariant,
} from "./ProductSliderScroll.tokens";

type ProductSliderScrollBottomCta = {
  label: string;
  href: string;
  ariaLabel?: string;
};

export type ProductSliderScrollProps = ProductSliderBaseProps & {
  /**
   * Preferred CTA API for new content.
   */
  bottomCta?: ProductSliderScrollBottomCta;

  /**
   * Backwards-compatible flat CTA props for JSON/page configs.
   */
  bottomCtaText?: string;
  bottomCtaHref?: string;
  bottomCtaAriaLabel?: string;

  bottomCtaAlign?: ProductSliderScrollBottomCtaAlign;
  bottomCtaVariant?: ProductSliderScrollBottomCtaVariant;
};

export default function ProductSliderScroll({
  items,
  Link,
  title = "Featured Products",
  eyebrow = "Products",
  description,
  currency = "AUD",
  ctaText = "View Product",
  filter,
  emptyTitle,
  emptyDescription,
  bottomCta,
  bottomCtaText,
  bottomCtaHref,
  bottomCtaAriaLabel,
  bottomCtaAlign = "center",
  bottomCtaVariant = "primary",
}: ProductSliderScrollProps) {
  const filteredItems = applyProductCollectionFilter(items, filter);

  const resolvedBottomCta = bottomCta ??
    (bottomCtaText && bottomCtaHref
      ? {
          label: bottomCtaText,
          href: bottomCtaHref,
          ariaLabel: bottomCtaAriaLabel,
        }
      : undefined);

  return (
    <section style={productSliderStyles.section}>
      <style>{responsiveCss}</style>

      <div style={productSliderStyles.headerWrap}>
        {eyebrow ? <p style={productSliderStyles.eyebrow}>{eyebrow}</p> : null}
        {title ? (
          <h2 className="product-slider-title" style={productSliderStyles.title}>
            {title}
          </h2>
        ) : null}
        {description ? <p style={productSliderStyles.description}>{description}</p> : null}
      </div>

      {filteredItems.length === 0 ? (
        <ProductEmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <div style={productSliderScrollStyles.contentWrap}>
          <div className="product-slider-scroll" style={productSliderStyles.scrollWrap}>
            <div className="product-slider-scroll-track" style={productSliderStyles.scrollTrack}>
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className="product-slider-card"
                  style={{ flex: "0 0 auto", display: "flex" }}
                >
                  <ProductSliderCard
                    item={item}
                    Link={Link}
                    currency={currency}
                    ctaText={ctaText}
                  />
                </div>
              ))}
            </div>
          </div>

          {resolvedBottomCta ? (
            <div style={getProductSliderScrollBottomCtaWrapStyle(bottomCtaAlign)}>
              <Link
                href={resolvedBottomCta.href}
                aria-label={resolvedBottomCta.ariaLabel}
                style={getProductSliderScrollBottomCtaButtonStyle(bottomCtaVariant)}
              >
                {resolvedBottomCta.label}
              </Link>
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}
